import { TableWidget } from '../TableWidget';
import { PieChartWidget } from '../categoricalCharts/PieChartWidget';
import {
  APITableWidgetAttributes,
  ArdoqId,
  HexColor,
  Maybe,
  WidgetDataSourceTypes,
  WidgetTypes,
} from '@ardoq/api-types';
import { NumberWidget } from '../NumberWidget/NumberWidget';
import { getNumberWidgetProps } from '../NumberWidget/getNumberWidgetProps';
import { getTableWidgetProps } from '../getTableWidgetProps';
import { enhancePartialScopeData } from '@ardoq/renderers';
import { BarChartWidget } from '../categoricalCharts/BarChartWidget';
import { getBarChartProps } from '../categoricalCharts/getBarChartProps';
import { getPieChartProps } from '../categoricalCharts/getPieChartProps';
import { StackedBarChartWidget } from '../timelineWidgets/StackedBarChartWidget';
import { getStackedBarChartWidgetProps } from '../timelineWidgets/getStackedBarChartOptions';
import { LineChartWidget } from '../timelineWidgets/LineChartWidget';
import { getLineChartProps } from '../timelineWidgets/getLineChartOptions';
import { BubbleChartWidget } from '../BubbleChartWidget';
import {
  BarChartRenderData,
  BubbleChartRenderData,
  CategoricalDrilldownData,
  GaugeWidgetRenderData,
  LineChartRenderData,
  NumberWidgetRenderData,
  PercentageWidgetRenderData,
  PieChartRenderData,
  StackedBarChartRenderData,
  TableWidgetRenderData,
} from '../renderTypes';
import { DashboardModes, OnWidgetDrilldown } from '../../types';
import { Locale } from '@ardoq/locale';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { EnhancedScopeData } from '@ardoq/data-model';
import { WidgetLinkedDashboardFilter } from '../types';
import { getPercentageWidgetProps } from '../PercentageWidget/getPercentageWidgetProps';
import { PercentageWidget } from '../PercentageWidget/PercentageWidget';
import { getGaugeWidgetProps } from '../GaugeWidget/getGaugeWidgetProps';
import { ChartBodyProps } from './ChartBodyProps';
import { GaugeWidget } from '../GaugeWidget/GaugeWidget';
import { type I18nLocale } from '@ardoq/i18n';

const shouldHaveClickHandler = (
  widgetData:
    | (PieChartRenderData & CategoricalDrilldownData)
    | (BarChartRenderData & CategoricalDrilldownData),
  dashboardMode: DashboardModes,
  onWidgetDrilldown?: OnWidgetDrilldown
): onWidgetDrilldown is OnWidgetDrilldown =>
  Boolean(
    dashboardMode === DashboardModes.VIEW &&
    widgetData.datasource.sourceType === WidgetDataSourceTypes.REPORT &&
    widgetData.fields[0] &&
    widgetData.permissions?.canExploreDatasource &&
    onWidgetDrilldown
  );

export type WidgetRenderDataProps =
  | NumberWidgetRenderData
  | PercentageWidgetRenderData
  | GaugeWidgetRenderData
  | (Omit<TableWidgetRenderData, 'scopeData'> &
      Pick<APITableWidgetAttributes, 'scopeData'>)
  | LineChartRenderData
  | StackedBarChartRenderData
  | (PieChartRenderData & CategoricalDrilldownData)
  | (BarChartRenderData & CategoricalDrilldownData)
  | (BubbleChartRenderData & { _id: ArdoqId });

export const getChartBodyProps = ({
  widgetRenderData,
  userLocale,
  dashboardMode,
  getComponentLinkHandler,
  getHasComponentReadPermission,
  colors,
  onDrilldown,
  activeLinkedDashboardFilters,
  dashboardId,
  selectedConfidentialFieldNames = [],
  i18nLocale,
}: {
  widgetRenderData: WidgetRenderDataProps;
  userLocale: Locale;
  dashboardMode: DashboardModes;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getHasComponentReadPermission?: (
    scopeData: EnhancedScopeData
  ) => HasComponentReadPermission;
  colors: Record<string, HexColor>;
  onDrilldown: OnWidgetDrilldown | undefined;
  activeLinkedDashboardFilters: WidgetLinkedDashboardFilter[];
  dashboardId: ArdoqId | undefined;
  selectedConfidentialFieldNames?: string[];
  i18nLocale: I18nLocale;
}): ChartBodyProps => {
  if (widgetRenderData.viewType === WidgetTypes.NUMBER) {
    return getNumberWidgetProps(widgetRenderData, userLocale);
  } else if (widgetRenderData.viewType === WidgetTypes.PERCENTAGE) {
    return getPercentageWidgetProps(
      widgetRenderData,
      userLocale,
      dashboardMode,
      i18nLocale
    );
  } else if (widgetRenderData.viewType === WidgetTypes.GAUGE) {
    return getGaugeWidgetProps({
      widgetRenderData,
      userLocale,
      dashboardMode,
      onWidgetDrilldown: onDrilldown,
      activeLinkedDashboardFilters,
      dashboardId,
      i18nLocale,
    });
  } else if (widgetRenderData.viewType === WidgetTypes.TABLE) {
    const enhancedScopeData = // Todo: move this higher up the chain
      'scopeData' in widgetRenderData && widgetRenderData.scopeData
        ? enhancePartialScopeData(widgetRenderData.scopeData)
        : null;

    return getTableWidgetProps({
      userLocale,
      i18nLocale,
      dashboardMode,
      widgetRenderData: {
        ...widgetRenderData,
        scopeData: enhancedScopeData,
      },
      getComponentLinkHandler,
      hasComponentReadPermission:
        (enhancedScopeData &&
          getHasComponentReadPermission?.(enhancedScopeData)) ||
        undefined,
      selectedConfidentialFieldNames,
    });
  } else if (widgetRenderData.viewType === WidgetTypes.LINE_CHART) {
    return getLineChartProps({
      widgetRenderData: widgetRenderData,
      dataPointColors: colors,
      userLocale,
    });
  } else if (widgetRenderData.viewType === WidgetTypes.STACKED_BAR_CHART) {
    return getStackedBarChartWidgetProps({
      widgetRenderData: widgetRenderData,
      colors,
      userLocale,
      i18nLocale,
    });
  } else if (widgetRenderData.viewType === WidgetTypes.PIE_CHART) {
    return getPieChartProps({
      widgetRenderData: widgetRenderData,
      widgetDrilldownData: widgetRenderData, //  TODO: split these up more precisely later
      colors,
      userLocale,
      activeLinkedDashboardFilters,
      dashboardId,
      i18nLocale,
      onWidgetDrilldown: shouldHaveClickHandler(
        widgetRenderData,
        dashboardMode,
        onDrilldown
      )
        ? onDrilldown
        : undefined,
    });
  } else if (widgetRenderData.viewType === WidgetTypes.BUBBLE_CHART) {
    return {
      ...widgetRenderData,
      hasRadiusField: Boolean(widgetRenderData.fields[2]),
    };
  }
  return getBarChartProps({
    widgetRenderData: widgetRenderData,
    widgetDrilldownData: widgetRenderData, //  TODO: split these up more precisely later
    colors,
    userLocale,
    activeLinkedDashboardFilters,
    dashboardId,
    onWidgetDrilldown: shouldHaveClickHandler(
      widgetRenderData,
      dashboardMode,
      onDrilldown
    )
      ? onDrilldown
      : undefined,
  });
};

const ChartBody = (
  props: ChartBodyProps & {
    dashboardContainerRef: Maybe<HTMLDivElement>;
    i18nLocale: I18nLocale;
  }
) => {
  const viewType = props.viewType;
  const { i18nLocale } = props;
  switch (viewType) {
    case WidgetTypes.NUMBER:
      return <NumberWidget {...props} i18nLocale={i18nLocale} />;
    case WidgetTypes.PERCENTAGE:
      return <PercentageWidget {...props} />;
    case WidgetTypes.GAUGE:
      return <GaugeWidget {...props} i18nLocale={i18nLocale} />;
    case WidgetTypes.TABLE:
      return <TableWidget {...props} />;
    case WidgetTypes.LINE_CHART:
      return <LineChartWidget {...props} />;
    case WidgetTypes.STACKED_BAR_CHART:
      return <StackedBarChartWidget {...props} i18nLocale={i18nLocale} />;
    case WidgetTypes.PIE_CHART:
      return <PieChartWidget {...props} i18nLocale={i18nLocale} />;
    case WidgetTypes.BAR_CHART:
      return <BarChartWidget {...props} />;
    case WidgetTypes.BUBBLE_CHART:
      return <BubbleChartWidget {...props} />;
    default:
      viewType satisfies never;
      return null;
  }
};

export { ChartBody };
